import { skipToken } from '@reduxjs/toolkit/query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Avatar, Box, Container, Paper, Typography } from '@mui/material'

import { getAvatarInitials } from 'utils/avatar'

import {
  useGetOrganizationByIdQuery,
  useGetOrganizationMembersQuery,
  useGetOrganizationTemplatesDefaultQuery,
  useGetOrganizationTemplatesSavedQuery,
} from 'services/organizations'
import { useGetWalletsCurrenciesQuery } from 'services/wallets'

import { AppRoute } from 'assets/const'
import { ReactComponent as EditIcon } from 'assets/svg/buttons/EditIcon.svg'

import BackButton from 'components/BackButton'
import { TModalVariant } from 'components/Modal/types'
import OrganizationActionModal from 'components/OrganizationActionModal'
import OrganizationMainInfo from 'components/OrganizationMainInfo'
import OrganizationMembers from 'components/OrganizationMembers'
import OrganizationWallets from 'components/OrganizationWallets'
import OrganizationWithdrawTemplates from 'components/OrganizationWithdrawTemplates'
import Preloader from 'components/Preloader'

import ErrorPage from 'pages/error'

import s from './styles'

const Organization = () => {
  const { t } = useTranslation()
  const { organizationId } = useParams()
  const [modalMode, setModalMode] = useState<TModalVariant>(null)

  const {
    data: organization,
    isLoading: isOrganizationLoading,
    isFetching: isOrganizationFetching,
  } = useGetOrganizationByIdQuery(organizationId || skipToken)

  const {
    data: members,
    isLoading: isMembersLoading,
    isFetching: isMembersFetching,
  } = useGetOrganizationMembersQuery(organizationId || skipToken)

  const { data: currencies = [], isLoading: isCurrenciesLoading } = useGetWalletsCurrenciesQuery()

  const { data: defaultTemplates, isLoading: isDefaultTemplatesLoading } =
    useGetOrganizationTemplatesDefaultQuery(organizationId || skipToken)

  const {
    data: savedTemplates,
    isLoading: isSavedTemplatesLoading,
    isFetching: isSavedTemplatesFetching,
    refetch: refetchSavedTemplates,
  } = useGetOrganizationTemplatesSavedQuery(organizationId || skipToken)

  if (
    isOrganizationLoading ||
    isMembersLoading ||
    isDefaultTemplatesLoading ||
    isCurrenciesLoading ||
    isSavedTemplatesLoading
  )
    return <Preloader />

  if (!organization || !organizationId) return <ErrorPage />

  return (
    <>
      {(isOrganizationFetching || isMembersFetching) && <Preloader />}
      <Container maxWidth="xl" disableGutters>
        <BackButton
          text={t('Pages.Organizations.BackToMyOrganizations')}
          link={`/${AppRoute.Organizations}`}
        />
        <Container maxWidth="lg" disableGutters>
          <Paper elevation={0} sx={s.paper}>
            <Box sx={s.mainInfo}>
              <Avatar alt="Avatar" src={''} sx={s.avatar}>
                <Typography variant="h1">{getAvatarInitials(organization.name)}</Typography>
              </Avatar>
              <Box>
                <Box sx={s.mainInfoTitleContainer}>
                  <Typography variant="h3">{organization.name}</Typography>
                  <EditIcon onClick={() => setModalMode('edit')} />
                </Box>
                <Box sx={s.mainInfoContentContainer}>
                  <OrganizationMainInfo organization={organization} />
                </Box>
              </Box>
            </Box>
            <OrganizationMembers members={members} />
          </Paper>
          <OrganizationWithdrawTemplates
            savedTemplates={savedTemplates}
            defaultTemplates={defaultTemplates}
            refetchSavedTemplates={refetchSavedTemplates}
            organizationId={organizationId}
            isSavedTemplatesFetching={isSavedTemplatesFetching}
          />
          <OrganizationWallets currencies={currencies} organizationId={organizationId} />
        </Container>
      </Container>
      {modalMode && (
        <OrganizationActionModal
          organizationName={organization.name}
          variant={'edit'}
          onClose={() => setModalMode(null)}
        />
      )}
    </>
  )
}

export default Organization
